/**
 * Repeatable location input for provider/clinic locations. Supports existing selection or new with Places autocomplete.
 * @agentic @benchmark/core
 */

import { useCallback, useState } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Collapse,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput as ManualTextInput,
} from "@mantine/core";
import type { ComboboxProps } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import {
  IconAlertTriangle,
  IconCheck,
  IconEdit,
  IconMapPin,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { PhoneInput } from "@benchmark/ui/components/atomic";
import {
  PlacesAutocomplete,
  type PlaceResult,
} from "@/components/common/forms/PlacesAutocomplete";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { hasValidAddress, type LocationFormData } from "@benchmark/core/types";
import { getStateOptions } from "@benchmark/core/constants";
import styles from "./LocationSection.module.css";

/**
 * Props for LocationSection component.
 * @agentic @benchmark/core
 */
type LocationSectionProps<T extends Record<string, unknown>> = {
  form: UseFormReturnType<T>;
  fieldPath: string;
  existingLocations: Array<{ value: string; label: string }>;
  loading?: boolean;
  title?: string;
  description?: string;
  /** Forwarded to all inner Select components (e.g. to pin dropdown position). */
  comboboxProps?: ComboboxProps;
};

/**
 * Renders repeatable location entries with existing select or new address creation.
 * @agentic @benchmark/core
 */
export const LocationSection = <T extends Record<string, unknown>>({
  form,
  fieldPath,
  existingLocations,
  loading = false,
  title,
  description,
  comboboxProps,
}: LocationSectionProps<T>) => {
  const locations =
    (form.values[fieldPath] as LocationFormData[] | undefined) || [];
  const [manualEditMode, setManualEditMode] = useState<Record<number, boolean>>(
    {},
  );

  const addLocation = () => {
    const newLocation: LocationFormData = {
      isNew: false,
      existingId: "",
      existingDisplay: "",
    };
    (form.insertListItem as (path: string, item: LocationFormData) => void)(
      fieldPath,
      newLocation,
    );
  };

  const removeLocation = (index: number) => {
    form.removeListItem(fieldPath, index);
    setManualEditMode((prev) => {
      const next: Record<number, boolean> = {};
      // Rebuild the state with shifted indices
      for (const [key, value] of Object.entries(prev)) {
        const keyIndex = Number(key);
        if (keyIndex < index) {
          // Keep indices before the removed one unchanged
          next[keyIndex] = value;
        } else if (keyIndex > index) {
          // Shift indices after the removed one down by 1
          next[keyIndex - 1] = value;
        }
        // Skip the removed index (keyIndex === index)
      }
      return next;
    });
  };

  const setNestedFieldValue = <V,>(path: string, value: V): void => {
    (form.setFieldValue as (path: string, value: V) => void)(path, value);
  };

  const toggleNewLocation = (index: number, isNew: boolean) => {
    if (isNew) {
      const updated: LocationFormData = {
        isNew: true,
        existingId: undefined,
        existingDisplay: undefined,
      };
      setNestedFieldValue(`${fieldPath}.${index}`, updated);
    } else {
      const updated: LocationFormData = {
        isNew: false,
        existingId: "",
        existingDisplay: "",
      };
      setNestedFieldValue(`${fieldPath}.${index}`, updated);
      setManualEditMode((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleExistingLocationChange = (
    index: number,
    value: string | null,
  ) => {
    const current = locations[index];
    const selectedOption = existingLocations.find((loc) => loc.value === value);
    const updated: LocationFormData = {
      ...current,
      existingId: value || undefined,
      existingDisplay: selectedOption?.label || undefined,
    };
    setNestedFieldValue(`${fieldPath}.${index}`, updated);
  };

  const handlePlaceSelect = useCallback(
    (index: number, place: PlaceResult) => {
      const current = locations[index];
      const updated: LocationFormData = {
        ...current,
        street: place.street || place.formattedAddress || "",
        city: place.city || "",
        state: place.stateCode || "",
        zip: place.postalCode || "",
        latitude: place.latitude,
        longitude: place.longitude,
        googlePlaceId: place.placeId,
      };
      setNestedFieldValue(`${fieldPath}.${index}`, updated);
      setManualEditMode((prev) => ({ ...prev, [index]: false }));
    },
    [fieldPath, locations, setNestedFieldValue],
  );

  const toggleManualEdit = (index: number) => {
    setManualEditMode((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const hasAddressData = (location: LocationFormData): boolean => {
    return Boolean(location.street || location.city);
  };

  const getMissingAddressFields = (location: LocationFormData): string[] => {
    const missing: string[] = [];
    if (!location.street) missing.push("street");
    if (!location.city) missing.push("city");
    if (!location.state) missing.push("state");
    if (!location.zip) missing.push("ZIP code");
    return missing;
  };

  return (
    <Box>
      {(title || description) && (
        <Box mb="sm">
          {title && (
            <Text size="sm" fw={500}>
              {title}
            </Text>
          )}
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </Box>
      )}

      <Stack gap="md">
        {locations.map((location: LocationFormData, index: number) => (
          <Paper key={index} p="md" withBorder>
            <Group justify="space-between" mb="sm">
              <Group gap="sm">
                <Group gap="xs">
                  <IconMapPin size={16} />
                  <Text size="sm" fw={500}>
                    Location {index + 1}
                  </Text>
                </Group>
                {/* Only show toggle for new/empty locations - hide for selected existing locations */}
                {(location.isNew || !location.existingId) && (
                  <Switch
                    size="xs"
                    label="New Location"
                    checked={location.isNew}
                    onChange={(e) =>
                      toggleNewLocation(index, e.currentTarget.checked)
                    }
                    classNames={{
                      root: styles.newLocationToggleRoot,
                      label: styles.newLocationToggleLabel,
                      track: styles.newLocationToggleTrack,
                    }}
                  />
                )}
              </Group>
              <ActionIcon
                color="red"
                variant="light"
                size="sm"
                onClick={() => removeLocation(index)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>

            {!location.isNew ? (
              <>
                <Select
                  placeholder={
                    loading
                      ? "Loading locations..."
                      : existingLocations.length === 0
                        ? "No existing locations available"
                        : "Select existing location"
                  }
                  data={existingLocations}
                  searchable
                  clearable
                  disabled={loading || existingLocations.length === 0}
                  value={location.existingId || null}
                  onChange={(value) =>
                    handleExistingLocationChange(index, value)
                  }
                  comboboxProps={comboboxProps}
                />
                {!loading && existingLocations.length === 0 && (
                  <Text size="xs" c="dimmed" mt={4}>
                    No existing locations found. Toggle "New Location" to create
                    one.
                  </Text>
                )}
              </>
            ) : (
              <Stack gap="sm">
                <FormGroup>
                  <FormField label="Location Name" required={location.isNew}>
                    <TextInput
                      placeholder="e.g., Main Office, Downtown Clinic"
                      {...form.getInputProps(`${fieldPath}.${index}.name`)}
                    />
                  </FormField>
                  <FormField label="Phone">
                    <PhoneInput
                      placeholder="Enter phone number"
                      {...form.getInputProps(`${fieldPath}.${index}.phone`)}
                    />
                  </FormField>
                </FormGroup>

                <FormField label="Address" required={location.isNew}>
                  <PlacesAutocomplete
                    value={location.street || ""}
                    onChange={(value) =>
                      setNestedFieldValue(`${fieldPath}.${index}.street`, value)
                    }
                    onPlaceSelect={(place) => handlePlaceSelect(index, place)}
                    placeholder="Search for an address..."
                    error={
                      form.errors[`${fieldPath}.${index}.street`] as
                        string | undefined
                    }
                  />
                </FormField>

                {hasAddressData(location) && location.googlePlaceId && (
                  <>
                    {hasValidAddress(location) ? (
                      <Alert
                        icon={<IconCheck size={14} />}
                        color="green"
                        variant="light"
                        py="xs"
                      >
                        <Text size="xs">
                          {location.street}, {location.city}, {location.state}{" "}
                          {location.zip}
                        </Text>
                      </Alert>
                    ) : (
                      <Alert
                        icon={<IconAlertTriangle size={14} />}
                        color="yellow"
                        variant="light"
                        py="xs"
                      >
                        <Text size="xs">
                          Some address fields are missing (
                          {getMissingAddressFields(location).join(", ")}). Click
                          "Edit Manually" below to fill them in.
                        </Text>
                      </Alert>
                    )}
                  </>
                )}

                {hasAddressData(location) &&
                  !location.googlePlaceId &&
                  !manualEditMode[index] && (
                    <Alert
                      icon={<IconAlertTriangle size={14} />}
                      color="orange"
                      variant="light"
                      py="xs"
                    >
                      <Text size="xs">
                        Address entered manually without geocoding. Select a
                        result from the dropdown for accurate coordinates, or
                        click "Edit Manually" to review.
                      </Text>
                    </Alert>
                  )}

                {(hasAddressData(location) || manualEditMode[index]) && (
                  <>
                    <Collapse in={manualEditMode[index]}>
                      <Stack gap="sm">
                        <FormField label="Address Line 2">
                          <TextInput
                            placeholder="Suite, apt, floor, etc."
                            {...form.getInputProps(
                              `${fieldPath}.${index}.line2`,
                            )}
                          />
                        </FormField>

                        <FormGroup>
                          <FormField label="City" required={location.isNew}>
                            <TextInput
                              placeholder="Enter city"
                              {...form.getInputProps(
                                `${fieldPath}.${index}.city`,
                              )}
                            />
                          </FormField>
                          <FormField label="State" required={location.isNew}>
                            <Select
                              placeholder="Select state"
                              data={getStateOptions()}
                              searchable
                              clearable
                              comboboxProps={comboboxProps}
                              {...form.getInputProps(
                                `${fieldPath}.${index}.state`,
                              )}
                            />
                          </FormField>
                          <FormField label="ZIP Code" required={location.isNew}>
                            <TextInput
                              placeholder="Enter ZIP code"
                              {...form.getInputProps(
                                `${fieldPath}.${index}.zip`,
                              )}
                            />
                          </FormField>
                        </FormGroup>

                        {typeof location.latitude === "number" &&
                          typeof location.longitude === "number" && (
                            <FormGroup>
                              <FormField label="Latitude">
                                <ManualTextInput
                                  value={location.latitude?.toString() || ""}
                                  disabled
                                  size="sm"
                                />
                              </FormField>
                              <FormField label="Longitude">
                                <ManualTextInput
                                  value={location.longitude?.toString() || ""}
                                  disabled
                                  size="sm"
                                />
                              </FormField>
                            </FormGroup>
                          )}
                      </Stack>
                    </Collapse>

                    <Button
                      variant="subtle"
                      size="xs"
                      leftSection={<IconEdit size={12} />}
                      onClick={() => toggleManualEdit(index)}
                    >
                      {manualEditMode[index] ? "Hide Details" : "Edit Manually"}
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>

      {locations.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No locations added yet. Click "Add Location" to get started.
        </Text>
      )}

      <Button
        mt="sm"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={addLocation}
        fullWidth
      >
        Add Location
      </Button>
    </Box>
  );
};
